import axios from 'axios';
import moment from 'moment';
import { createAction } from '@reduxjs/toolkit';

import { responseEntityMapper, responseListMapper } from '@qwealth/qcore';
import {
  ActiveStatus,
  CompletedStatus,
  DocusignEnvelope,
  OpeningAccount,
  DocusignRegistration,
  PendingApprovalStatus,
  VoidedStatus,
  dateFormat,
  REACT_APP_QWEALTH_API_V3,
} from '@qwealth/qdata';
import { putOpeningAccount, saveOrUpdateAccountRequest } from '@qwealth/kyc';

import { errorHandler, REACT_APP_QWEALTH_API } from '../../services/axiosService';
import { D1OpeningAccountPayload, D2SendPayload, D2DownloadPayload } from '../models/OpeningAccountFees';
import { D2Envelope } from '../models/D2Envelope';
import { removeOpeningAccount } from './openingAccounts';
import { AppThunkDispatch } from '../store';

const PUT_DOCUSIGN_ENVELOPES = 'PUT_DOCUSIGN_ENVELOPES';
const PUT_ACTIVE_DOCUSIGN_ENVELOPE = 'PUT_ACTIVE_DOCUSIGN_ENVELOPE';
const REMOVE_ACTIVE_DOCUSIGN_ENVELOPE = 'REMOVE_ACTIVE_DOCUSIGN_ENVELOPE';
const PUT_REGISTRATION_ON_ACTIVE_ENVELOPE = 'PUT_REGISTRATION_ON_ACTIVE_ENVELOPE';
const PUT_ACTIVE_DOCUSIGN_ENVELOPES = 'PUT_ACTIVE_DOCUSIGN_ENVELOPES';

const getErrorMessage = (error): string | undefined => error?.response?.data?.message ?? error?.response?.data;

export const putDocusignEnvelopes = createAction<DocusignEnvelope[]>(PUT_DOCUSIGN_ENVELOPES);
export const removeActiveDocusignEnvelope = createAction(REMOVE_ACTIVE_DOCUSIGN_ENVELOPE);
export const putActiveDocusignEnvelope = createAction<DocusignEnvelope>(PUT_ACTIVE_DOCUSIGN_ENVELOPE);
export const putRegistrationOnActiveEnvelope = createAction<DocusignRegistration>(PUT_REGISTRATION_ON_ACTIVE_ENVELOPE);
export const putActiveDocusignEnvelopes = createAction<D2Envelope[]>(PUT_ACTIVE_DOCUSIGN_ENVELOPES);

export const loadAllDocusignEnvelopes = () => (dispatch: AppThunkDispatch) =>
  axios
    .get(`${REACT_APP_QWEALTH_API}/docusign/envelopes`)
    .then(resp => responseListMapper<DocusignEnvelope>(resp))
    .then(envelopes => dispatch(putDocusignEnvelopes(envelopes)))
    .catch(error => errorHandler(dispatch, 'Docusign envelope loading error')(error));

export const loadDocusignEnvelopeForHousehold =
  (householdQID: string) =>
  (dispatch: AppThunkDispatch): Promise<any> =>
    axios
      .get(`${REACT_APP_QWEALTH_API}/docusign/hhLatestEnvelope/${householdQID}`)
      .then(resp => responseEntityMapper<DocusignEnvelope>(resp))
      .then(envelope => {
        if (envelope && [PendingApprovalStatus, ActiveStatus, CompletedStatus].includes(envelope.envelopeStatus)) {
          envelope.nbinRegistration = stripTimeFromRegistrationsNotes(envelope.nbinRegistration);
          dispatch(putActiveDocusignEnvelope(envelope));
        } else {
          dispatch(removeActiveDocusignEnvelope());
        }
      })
      .catch(error => errorHandler(dispatch, 'API Error: Failed retrieving docusign envelope for household')(error));

export const sendDocusignEnvelopeForApproval = (payload: D1OpeningAccountPayload) => (dispatch: AppThunkDispatch) =>
  axios
    .post(`${REACT_APP_QWEALTH_API}/docusign/embedded/sending`, payload, { timeout: moment.duration(2, 'minutes').asMilliseconds() })
    .then(() => {
      const promises: Array<Promise<OpeningAccount>> = [
        dispatch(loadDocusignEnvelopeForHousehold(payload.QID)),
        ...payload.opening_accounts.map(openingAccount => {
          const accountData: OpeningAccount = { ...openingAccount, status: 'Pending Approval' };
          return dispatch(saveOrUpdateAccountRequest(accountData));
        }),
      ];
      return Promise.all(promises);
    })
    .catch(error => Promise.reject(getErrorMessage(error)));

export const cancelDocusignEnvelope =
  ({ docusignEnvelopeId, accounts }: { docusignEnvelopeId: string; accounts: Array<OpeningAccount> }) =>
  (dispatch: AppThunkDispatch) =>
    axios
      .post(`${REACT_APP_QWEALTH_API}/docusign/envelope/cancel`, {
        envelope_id: docusignEnvelopeId,
        envelopeStatus: VoidedStatus,
        reason: 'No reason just cancel the envelope for now',
      })
      .then(resp => responseEntityMapper<DocusignEnvelope>(resp))
      .then(envelope => dispatch(putActiveDocusignEnvelope(envelope)))
      .then(() => {
        const promises: Array<Promise<OpeningAccount>> = [
          ...accounts.map(openingAccount => {
            const accountData: OpeningAccount = { ...openingAccount, status: 'Created' };
            dispatch(putOpeningAccount(accountData));
            return dispatch(saveOrUpdateAccountRequest(accountData));
          }),
        ];
        return Promise.all(promises);
      })
      .catch(error => Promise.reject(getErrorMessage(error)));

export const resendDocusignEnvelope =
  ({ docusignEnvelopeId }: { docusignEnvelopeId: string }) =>
  (dispatch: AppThunkDispatch) =>
    axios
      .post(`${REACT_APP_QWEALTH_API}/docusign/envelope/resend`, {
        envelope_id: docusignEnvelopeId,
      })
      .then(resp => responseEntityMapper<DocusignEnvelope>(resp))
      .then(envelope => dispatch(putActiveDocusignEnvelope(envelope)))
      .catch(error => Promise.reject(getErrorMessage(error)));

export const archiveDocusignEnvelope =
  ({ envelopeId, accounts }: { envelopeId: number; accounts: Array<OpeningAccount> }) =>
  (dispatch: AppThunkDispatch) =>
    axios
      .patch(`${REACT_APP_QWEALTH_API}/docusign/envelope/${envelopeId}`, {
        envelopeStatus: 'Archived',
      })
      .then(resp => responseEntityMapper<DocusignEnvelope>(resp))
      .then(() => dispatch(removeActiveDocusignEnvelope()))
      .then(() => {
        const promises: Array<Promise<OpeningAccount>> = [
          ...accounts.map(openingAccount => {
            const accountData: OpeningAccount = { ...openingAccount, status: 'Archived' };
            if (accountData.id) {
              dispatch(removeOpeningAccount(accountData.id));
            }

            return dispatch(saveOrUpdateAccountRequest(accountData));
          }),
        ];
        return Promise.all(promises);
      })
      .catch(error => Promise.reject(getErrorMessage(error)));

export const saveDocusignEnvelope = (envelope: Partial<DocusignEnvelope>) => (dispatch: AppThunkDispatch) => {
  const request = !envelope.id
    ? axios.post(`${REACT_APP_QWEALTH_API}/docusign/envelope/`, envelope)
    : axios.patch(`${REACT_APP_QWEALTH_API}/docusign/envelope/${envelope.id}`, envelope);
  return request
    .then(response => responseEntityMapper<DocusignEnvelope>(response))
    .then(response => {
      const newEnvelope = { ...envelope, ...response };
      dispatch(putActiveDocusignEnvelope(newEnvelope));
      return Promise.resolve(newEnvelope);
    })
    .catch(error => {
      errorHandler(dispatch)(error);
      return Promise.reject(null);
    });
};

export const saveDocusignRegistration = (registration: Partial<DocusignRegistration>) => (dispatch: AppThunkDispatch) => {
  const request = !registration.id
    ? axios.post(`${REACT_APP_QWEALTH_API}/nbinRegistration/`, registration)
    : axios.patch(`${REACT_APP_QWEALTH_API}/nbinRegistration/${registration.id}`, registration);
  return request
    .then(response => responseEntityMapper<DocusignRegistration>(response))
    .then(newRegistration => {
      newRegistration = stripTimeFromRegistrationNotes(newRegistration);
      dispatch(putRegistrationOnActiveEnvelope(newRegistration));
      return Promise.resolve(newRegistration);
    })
    .catch(error => errorHandler(dispatch, 'API Error: Failed saving registration')(error));
};

export const stripTimeFromRegistrationNotes = (registration: DocusignRegistration): DocusignRegistration => ({
  ...registration,
  nbinRegistrationNotes: registration.nbinRegistrationNotes.map(note => ({
    ...note,
    startTime: moment(note.startTime).format(dateFormat),
  })),
});

export const stripTimeFromRegistrationsNotes = (registrations: DocusignRegistration[]): DocusignRegistration[] =>
  registrations.map(registration => stripTimeFromRegistrationNotes(registration));

export const downloadDocusign2Forms = async (payload: D2DownloadPayload) => {
  const config = { responseType: 'blob' as const };
  try {
    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/account-opening/download`, payload, config);
    return await response.data;
  } catch (error) {
    // @ts-ignore
    const errorText = await error?.response?.data?.text();
    return Promise.reject(errorText);
  }
};

export const sendUsingDocusign2 = async (payload: D2SendPayload) => {
  try {
    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/account-opening/send`, payload);
    return response.data.envelopes.map((envelope: { envelope_id: string }) => envelope.envelope_id);
  } catch (error) {
    return await Promise.reject(getErrorMessage(error));
  }
};

export const voidIncompleteD2Envelopes =
  ({ docusignEnvelopeId, accounts }: { docusignEnvelopeId: string; accounts: Array<OpeningAccount> }) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    try {
      await axios.post(`${REACT_APP_QWEALTH_API_V3}/docusign/envelope/batchVoidIncomplete`, { docusignEnvelopeId });
      const promises: Array<Promise<OpeningAccount>> = accounts.map(openingAccount => {
        const accountData: OpeningAccount = { ...openingAccount, status: 'Created' };
        dispatch(putOpeningAccount(accountData));
        return dispatch(saveOrUpdateAccountRequest(accountData));
      });
      await Promise.all(promises);
    } catch (error) {
      throw getErrorMessage(error);
    }
  };

export const loadD2EnvelopesForHousehold =
  (householdQID: string) =>
  async (dispatch: AppThunkDispatch): Promise<D2Envelope[] | null> => {
    try {
      const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/docusign/latestEnvelopeBatch/${householdQID}`);
      const envelopes: D2Envelope[] = response.data;
      dispatch(putActiveDocusignEnvelopes(envelopes));
      return envelopes;
    } catch (error) {
      errorHandler(dispatch, 'API Error: Failed fetching D2 active envelopes for household')(error);
      return null;
    }
  };

export const loadEnvelopeByDocusignEnvelopeId = (envelopeId: string) => async (dispatch: AppThunkDispatch) => {
  return axios
    .get(`${REACT_APP_QWEALTH_API}/docusign/envelopeByDocusignEnvelopeId/${envelopeId}`)
    .then(response => responseEntityMapper<DocusignEnvelope>(response))
    .then(envelope => {
      if (envelope && [PendingApprovalStatus, ActiveStatus, CompletedStatus].includes(envelope.envelopeStatus)) {
        envelope.nbinRegistration = stripTimeFromRegistrationsNotes(envelope.nbinRegistration);
        dispatch(putActiveDocusignEnvelope(envelope));
      } else {
        dispatch(removeActiveDocusignEnvelope());
      }
    })
    .catch(error => errorHandler(dispatch, 'API Error: Failed fetching envelope by docusignEnvelopeId')(error));
};
